import React, { useEffect, useState } from "react";
import useCasesData from "@/src/useData/useCasesData";
import S from "./AllCases.styled";
import {
  BackgroundImage,
  Icon,
  LoadMoreButton,
  TextButton,
} from "@/styles/components";
import { IWPImage, IWPLink } from "@/src/types";
import { Flex, MainInner } from "@/styles/global";
import { H3 } from "@/styles/typography";
import { motion } from "framer-motion";

type Cases = {
  title: string;
  uri: string;
  date: string;
  casesInfo: {
    featuredImage: IWPImage;
    content: string;
    localOrExternal: string;
    externalUrl: string;
  };
  partners: {
    nodes: {
      name: string;
      slug: string;
    }[];
  };
};

export interface AllCasesProps {
  blockId: string;
  link: IWPLink;
}

const AllCases: React.FC<AllCasesProps> = ({ link, blockId = "" }) => {
  const cases = useCasesData();
  const [currentPage, setCurrentPage] = useState(0);
  const [paginatedCases, setPaginatedCases] = useState<Cases[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("All");

  // Extract all category names from the data
  const allCategoryNames = cases.flatMap((node) =>
    node.caseCategories.nodes.map((category) => category.name)
  );

  // Remove duplicates by creating a Set and converting it back to an array
  const uniqueCategoryNames = Array.from(new Set(allCategoryNames));

  // useEffect to update the displayed cases based on activeCategory and pagination
  useEffect(() => {
    if (activeCategory === "All") {
      // When "All" is active, apply pagination.
      const newCases = cases.slice(0, (currentPage + 1) * 15);
      setPaginatedCases(newCases);
    } else {
      // When a specific category is active, filter cases without pagination.
      const filteredCases = cases.filter((node) =>
        node.caseCategories.nodes.some(
          (category) => category.name === activeCategory
        )
      );
      setPaginatedCases(filteredCases);
    }
  }, [currentPage, cases, activeCategory]);

  // Optional: reset pagination when switching back to "All"
  useEffect(() => {
    if (activeCategory === "All") {
      setCurrentPage(0);
    }
  }, [activeCategory]);
  const renderCard = (cases: Cases, key: number) => {
    const {
      title,
      uri,
      date,
      partners,
      casesInfo: { featuredImage, localOrExternal, externalUrl },
    } = cases;

    const CardContent = () => (
      <>
        {" "}
        <BackgroundImage image={featuredImage} />
        <S.CardTextWrapper>
          <Flex $gap={16}>
            {/* <S.CardDate color="white">{date}</S.CardDate> */}
            {partners.nodes.length ? (
              <S.CardDate color="white">
                <span style={{ fontWeight: 700, marginRight: 4 }}>Partner</span>
                {partners?.nodes[0]?.name}
              </S.CardDate>
            ) : null}
          </Flex>
          <H3 color="white">{title}</H3>
          <S.CardReadMoreWrapper>
            <S.CardReadMore color="white">Read more</S.CardReadMore>
            <Icon src="ArrowRightOutline" color="white" />
          </S.CardReadMoreWrapper>
        </S.CardTextWrapper>
      </>
    );
    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {localOrExternal === "external" ? (
          <S.CardExternal
            href={externalUrl}
            target="_blank"
            $external
            rel="noreferrer"
          >
            <CardContent />
          </S.CardExternal>
        ) : (
          <S.Card to={uri} $external={false}>
            <CardContent />
          </S.Card>
        )}
      </motion.div>
    );
  };

  const caseCategoryFilter = uniqueCategoryNames.map((category, i) => {
    return (
      <S.Filter
        key={i}
        $active={activeCategory === category}
        onClick={() => setActiveCategory(category)}
      >
        {category}
      </S.Filter>
    );
  });

  return (
    <S.Section id={blockId}>
      <MainInner>
        {link && (
          <TextButton
            wpLink={link}
            color="black"
            underlined
            left={
              <Icon
                src="ArrowLeftOutline"
                width={15}
                margin="0 16px 0 0"
                color="red"
              />
            }
          />
        )}
      </MainInner>
      <S.ScrollContainer>
        <S.Filters>
          <Flex $gap={16} $desktopGap={16}>
            <S.Filter
              $active={activeCategory === "All"}
              onClick={() => setActiveCategory("All")}
            >
              All
            </S.Filter>
            {caseCategoryFilter}
          </Flex>
        </S.Filters>
      </S.ScrollContainer>
      <MainInner>
        <S.Container>
          {paginatedCases.map((caseItem, i) => renderCard(caseItem, i))}
        </S.Container>
        {paginatedCases.length < cases.length && activeCategory === "All" && (
          <Flex $center $width="100%" $margin="24px 0">
            <LoadMoreButton
              label="Load more"
              onClick={() => setCurrentPage((prevPage) => prevPage + 1)}
            />
          </Flex>
        )}
      </MainInner>
    </S.Section>
  );
};

export default AllCases;
